import {
  Container, Title, Text, Stack, Table, Badge, Group, Button,
  LoadingOverlay, Card, Grid, ThemeIcon, Divider, Alert, Progress,
} from '@/components/ui';
import { ActionIcon } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconTrash, IconArrowLeft, IconMessageCircle, IconUpload,
  IconFileText, IconCheck, IconClockHour4, IconFiles, IconFile,
  IconBrain, IconAlertCircle,
} from '@tabler/icons-react';
import { FileUploader } from '@/components/ccms-components/file-management';
import type { FileInfo } from '@/components/ccms-components/file-management/types';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAiDocumentService } from '@actschurch/ccms-sdk/react';
import { useEnrichedAuth } from '../../../../hooks/useEnrichedAuth';
import type { AiDocument } from '@actschurch/ccms-sdk';

function formatDate(timestamp: number): string {
  if (!timestamp) return '-';
  const str = String(timestamp);
  if (str.length === 14) {
    const year = str.slice(0, 4);
    const month = str.slice(4, 6);
    const day = str.slice(6, 8);
    const hour = str.slice(8, 10);
    const min = str.slice(10, 12);
    return `${year}-${month}-${day} ${hour}:${min}`;
  }
  return str;
}

export default function DocumentLibraryPage() {
  const navigate = useNavigate();
  const documentService = useAiDocumentService();
  const queryClient = useQueryClient();
  const { user } = useEnrichedAuth();

  const { data: documents, isLoading } = useQuery({
    queryKey: ['ai', 'documents'],
    queryFn: () => documentService.listDocuments(),
    enabled: !!user,
  });

  // Step 2: After FileUploader uploads to File Management (S3),
  // tell the AI service to index the file that's now in S3
  const indexMutation = useMutation({
    mutationFn: (fileInfo: FileInfo) =>
      documentService.indexDocument({
        fileId: fileInfo.id,
        fileName: fileInfo.fileName,
        url: fileInfo.url ?? '',
        mimeType: fileInfo.mimeType,
        fileSize: fileInfo.fileSize,
      }),
    onSuccess: (result) => {
      notifications.show({
        title: 'Document indexed',
        message: `${result.fileName} — ${result.chunkCount} chunks created`,
        color: 'green',
      });
      queryClient.invalidateQueries({ queryKey: ['ai', 'documents'] });
    },
    onError: (err: Error) => {
      notifications.show({
        title: 'Indexing failed',
        message: err.message,
        color: 'red',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (documentId: number) => documentService.deleteDocument(documentId),
    onSuccess: () => {
      notifications.show({ title: 'Deleted', message: 'Document removed', color: 'blue' });
      queryClient.invalidateQueries({ queryKey: ['ai', 'documents'] });
    },
    onError: (err: Error) => {
      notifications.show({ title: 'Delete failed', message: err.message, color: 'red' });
    },
  });

  // Step 1: FileUploader uploads to File Management → S3
  // Step 2: On complete, trigger AI indexing
  const handleUploadComplete = (files: FileInfo[]) => {
    files.forEach((fileInfo) => {
      notifications.show({
        title: 'File uploaded',
        message: `${fileInfo.fileName} uploaded to storage. Starting AI indexing...`,
        color: 'blue',
      });
      indexMutation.mutate(fileInfo);
    });
  };

  const totalDocs = documents?.length ?? 0;
  const indexedCount = documents?.filter((d: AiDocument) => d.isIndexed).length ?? 0;
  const processingCount = documents?.filter((d: AiDocument) => !d.isIndexed).length ?? 0;

  return (
    <Container fluid py="xl" pos="relative">
      <LoadingOverlay visible={isLoading} />
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" wrap="wrap">
          <div>
            <Title order={2}>Document Library</Title>
            <Text size="sm" c="dimmed">Upload and manage documents for AI Q&amp;A.</Text>
          </div>
          <Group>
            <Button
              variant="light"
              leftSection={<IconMessageCircle size={16} />}
              onClick={() => navigate('/app/ai/documents/qa')}
            >
              Ask Questions
            </Button>
            <Button
              variant="default"
              leftSection={<IconArrowLeft size={16} />}
              onClick={() => navigate('/app/ai')}
            >
              Back to AI
            </Button>
          </Group>
        </Group>

        {/* Stat cards */}
        <Grid>
          <Grid.Col span={{ base: 6, md: 4 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group wrap="nowrap">
                <ThemeIcon size={48} radius="md" variant="light" color="blue">
                  <IconFiles size={24} />
                </ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Documents</Text>
                  <Text size="xl" fw={700}>{totalDocs}</Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 6, md: 4 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group wrap="nowrap">
                <ThemeIcon size={48} radius="md" variant="light" color="green">
                  <IconCheck size={24} />
                </ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Indexed</Text>
                  <Text size="xl" fw={700}>{indexedCount}</Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 6, md: 4 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group wrap="nowrap">
                <ThemeIcon size={48} radius="md" variant="light" color="yellow">
                  <IconClockHour4 size={24} />
                </ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Processing</Text>
                  <Text size="xl" fw={700}>{processingCount}</Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>
        </Grid>

        {/* Upload section — uses File Management for S3 upload, then AI service for indexing */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group justify="space-between" mb="sm">
            <Group gap="xs">
              <IconUpload size={20} />
              <Title order={4}>Upload Documents</Title>
            </Group>
            <Badge variant="light" color="teal">AI Indexing</Badge>
          </Group>
          <Divider mb="sm" />
          <Text size="xs" c="dimmed" mb="xs">
            Supports PDF, Word, Excel, PowerPoint, text, markdown, CSV and RTF files.
          </Text>
          <FileUploader
            domain="AIDocuments"
            recordId="0"
            multiple
            maxSize={50 * 1024 * 1024}
            onUploadComplete={handleUploadComplete}
            onUploadError={(error) => {
              notifications.show({
                title: 'Upload failed',
                message: error?.message ?? 'File upload failed',
                color: 'red',
              });
            }}
          />

          {/* Indexing progress indicator */}
          {indexMutation.isPending && (
            <Alert
              icon={<IconBrain size={20} />}
              color="blue"
              title="AI Indexing in progress..."
              mt="sm"
            >
              <Stack gap="xs">
                <Text size="sm">
                  Chunking document and generating embeddings. This may take a moment for large files.
                </Text>
                <Progress value={100} animated size="sm" color="blue" />
              </Stack>
            </Alert>
          )}

          {indexMutation.isError && (
            <Alert
              icon={<IconAlertCircle size={20} />}
              color="red"
              title="Indexing failed"
              mt="sm"
            >
              <Text size="sm">{indexMutation.error?.message ?? 'An error occurred during document indexing.'}</Text>
            </Alert>
          )}

          {indexMutation.isSuccess && (
            <Alert
              icon={<IconCheck size={20} />}
              color="green"
              title="Document indexed successfully"
              mt="sm"
            >
              <Text size="sm">
                {indexMutation.data?.fileName} — {indexMutation.data?.chunkCount} chunks created and ready for Q&A.
              </Text>
            </Alert>
          )}
        </Card>

        {/* Empty state */}
        {documents && documents.length === 0 && !isLoading && (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Stack align="center" gap="sm" py="lg">
              <ThemeIcon size={48} radius="xl" variant="light" color="gray">
                <IconFile size={24} />
              </ThemeIcon>
              <Text c="dimmed">No documents uploaded yet. Upload your first document above.</Text>
            </Stack>
          </Card>
        )}

        {/* Document table */}
        {documents && documents.length > 0 && (
          <Card shadow="sm" padding={0} radius="md" withBorder>
            <Group justify="space-between" p="lg" pb="sm">
              <Group gap="xs">
                <IconFileText size={20} />
                <Title order={4}>Document List</Title>
              </Group>
              <Badge variant="light" color="blue">{documents.length} documents</Badge>
            </Group>
            <Divider />
            <Table tableVariant="striped">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>File Name</Table.Th>
                  <Table.Th>Chunks</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {documents.map((doc: AiDocument) => (
                  <Table.Tr key={doc.documentID}>
                    <Table.Td>
                      <Group gap="xs">
                        <IconFile size={16} />
                        <Text size="sm">{doc.fileName}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>{doc.chunkCount}</Table.Td>
                    <Table.Td>
                      <Badge variant="light" color={doc.isIndexed ? 'green' : 'yellow'} size="sm">
                        {doc.isIndexed ? 'Indexed' : 'Processing'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">{formatDate(doc.createdDateTime)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        size="sm"
                        loading={deleteMutation.isPending}
                        onClick={() => deleteMutation.mutate(doc.documentID)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Card>
        )}
      </Stack>
    </Container>
  );
}
